//
// Copyright (c) 2025 Comcast
//

import { Fragment } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import * as sessionUserSelector from 'selectors/sessionUser'
import { userTimeZoneFormat } from 'utils/time'

import Tooltip from 'components/Shared/Tooltip/Index'
import ExpandRowIcon from 'components/Shared/TableNew/ExpandRowIcon'
import TableCell from 'components/Shared/TableNew/TableCell'
import EnabledIcon from 'components/Shared/EnabledIcon/Index'
import PartnersList from 'components/Shared/PartnersList/Index'
import MarkedText from 'components/Shared/MarkedText/Index'
import Badge from 'components/Shared/Badge/Index'
import TicketTypeBadge from 'components/App/TicketsPage/shared/TicketTypeBadge'
import InfoBadge from 'components/App/TicketsPage/shared/TicketsTable/InfoBadge'

const MAX_CHILDREN_IN_TOOLTIP = 5

const IdCell = styled(TableCell)`
  && {
    white-space: nowrap;
  }
  padding-left: 8px;
`

const IdCellContainer = styled.div`
  display: flex;
  align-items: center;
  ${props => props.$isInChildRow && css`
    padding-left: 8px;
  `}
`

const PartnerIdCell = styled(TableCell)`
  && {
    max-width: 250px;
    white-space: nowrap;
    word-break: break-word;
    overflow: visible;
  }
`

const StyledLink = styled(Link)`
  font-weight: 600;
  ${props => props.$checkableRows && css`
    pointer-events: none;
  `}
`

const InfoBadges = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 24px;
  padding-right: 12px;
`

const StyledMarkedText = styled.div`
  white-space: pre-line;
`

const TableCells = ({
  ticket, ticketUrl, partnerStatusesMap,
  searchValue, checkableRows, ticketBonding, checkedTicketIds,
  withExpandableRows, isExpanded, onExpandToggle, isInChildRow,
}) => {
  const { t } = useTranslation()
  const userTimeZone = useSelector(sessionUserSelector.timezone)

  const defaultCellContent = (value, fieldType) => {
    let cellContent

    if (fieldType === 'datetime') {
      cellContent = userTimeZoneFormat(userTimeZone, value)
    } else {
      let fieldValue = value
      if (fieldValue instanceof Array) {
        fieldValue = fieldValue.join(', ')
      } else {
        fieldValue = value?.toString()
      }
      cellContent = (
        <StyledMarkedText>
          <MarkedText text={fieldValue} markedPart={searchValue} />
        </StyledMarkedText>
      )
    }

    return cellContent
  }

  const childTicketsCount = ticket.childTicketsCount
  const isExpandable = Boolean(withExpandableRows && ticket.hierarchyEnabled && childTicketsCount)
  const showChildrenBadge = ticket.hierarchyEnabled

  let childTicketsIds
  if (ticket.hierarchyEnabled && ticket.childTickets) {
    childTicketsIds = ticket.childTickets.slice(0, MAX_CHILDREN_IN_TOOLTIP).map(childTicket => childTicket.number).join(', ')
  }

  const tooltipId = 'table_tooltip_' + ticket.id

  const getTicketFields = () => {
    const tableFields = ticket.tableFields.map(ticketTableField => {
      const { name, value, fieldType } = ticketTableField

      switch (name) {
        case 'id':
          return (
            <Fragment key={name}>
              {checkableRows && (
                <TableCell align='center' width='44px'>
                  {checkedTicketIds.includes(ticket.id) && <EnabledIcon enabled />}
                </TableCell>
              )}
              <IdCell>
                <IdCellContainer $isInChildRow={isInChildRow}>
                  <TicketTypeBadge ticketType={ticket.type} tooltipId={tooltipId} />
                  <MarkedText text={value} markedPart={searchValue} />
                  {isExpandable && <ExpandRowIcon isExpanded={isExpanded} onExpandToggle={onExpandToggle} />}
                </IdCellContainer>
                <Tooltip id={tooltipId} />
              </IdCell>
            </Fragment>
          )
        case 'partner_id':
          return (
            <Fragment key={name}>
              {ticketBonding === 'enabled' && (
                <PartnerIdCell>
                  <PartnersList partnerIds={value} defaultValue='' markedPart={searchValue} />
                </PartnerIdCell>
              )}
            </Fragment>
          )
        case 'status':
          return (
            <TableCell key={name} noPadding>
              <Badge big color={partnerStatusesMap[value]?.color}>
                {value}
              </Badge>
            </TableCell>
          )
        case 'title':
          return (
            <TableCell key={name}>
              <StyledLink
                to={ticketUrl}
                $checkableRows={checkableRows}
                onClick={e => e.stopPropagation()}
              >
                <MarkedText text={value} markedPart={searchValue} />
              </StyledLink>
            </TableCell>
          )
        default:
          return (
            <TableCell key={name}>
              {defaultCellContent(value, fieldType)}
            </TableCell>
          )
      }
    })
    return tableFields
  }

  const attachmentsCount = ticket.attachmentsCount
  const commentsCount = ticket.commentsCount

  const tableTicketFields = getTicketFields()

  return (
    <>
      {tableTicketFields}
      <TableCell>
        <Tooltip id={`count-badge-tooltip-${ticket.id}`} />
        <InfoBadges>
          {showChildrenBadge && (
            <InfoBadge
              iconName='icon-tree'
              tooltip={
                t('ticketsPage.tooltipChildTickets', {
                  count: childTicketsCount,
                  childTicketsIds: childTicketsIds,
                }) +
                (childTicketsCount > MAX_CHILDREN_IN_TOOLTIP
                  ? t('ticketsPage.tooltipChildTicketsMore', { count: childTicketsCount - MAX_CHILDREN_IN_TOOLTIP })
                  : '')
              }
              tooltipId={`count-badge-tooltip-${ticket.id}`}
              url={ticketUrl + '#children'}
            >
              {childTicketsCount}
            </InfoBadge>
          )}
          <InfoBadge
            iconName='icon-attachments'
            tooltip={t('ticketsPage.attachmentsCount', { count: attachmentsCount })}
            tooltipId={`count-badge-tooltip-${ticket.id}`}
            url={ticketUrl + '#attachments'}
          >
            {attachmentsCount}
          </InfoBadge>
          <InfoBadge
            iconName='icon-comments'
            tooltip={t('ticketsPage.commentsCount', { count: commentsCount })}
            tooltipId={`count-badge-tooltip-${ticket.id}`}
            url={ticketUrl + '#comments'}
          >
            {commentsCount}
          </InfoBadge>
        </InfoBadges>
      </TableCell>
    </>
  )
}

TableCells.propTypes = {
  ticket: PropTypes.object.isRequired,
  ticketUrl: PropTypes.string.isRequired,
  partnerStatusesMap: PropTypes.object.isRequired,
  searchValue: PropTypes.string,
  checkableRows: PropTypes.bool,
  ticketBonding: PropTypes.string,
  checkedTicketIds: PropTypes.array,
  withExpandableRows: PropTypes.bool,
  isExpanded: PropTypes.bool,
  onExpandToggle: PropTypes.func,
  isInChildRow: PropTypes.bool,
}

export default TableCells
